import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Users } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { queryKeys } from '@/lib/api/queryKeys';
import { xurveyApi, type OptionCount } from '@/lib/api/xurvey';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { ramped, useScrollRamp } from '@/features/insights/motion/useScrollRamp';
import { useTypewriter } from '@/features/insights/motion/useTypewriter';
import { celdasWaffle, dimensionesDe, dominante, titularDe, type Dimension } from './dimensiones';
import { useScopeDeployments } from './useScopeDeployments';

/**
 * «A quién llegamos»: la demografía de un alcance —un evento, un proyecto,
 * o lo que filtre Insights— leída de las RESPUESTAS de las encuestas.
 *
 * No hay una lista fija de gráficas. Cada marca pregunta lo suyo por paso, así
 * que el informe lee las preguntas de elección que trae la encuesta y a cada
 * una le asigna la gráfica que el diseño de dashboards reservó para su
 * familia (`dimensiones.ts`): escalera de edad, rosquilla de país, balanza de
 * género, waffle de origen, medidor de sí/no, y lista con barras para lo
 * demás. Si mañana una encuesta pregunta otra cosa, sale como lista sin que
 * nadie toque este fichero.
 *
 * Los números los suma el servidor (`aggregate` con `publicLinkIds`): aquí
 * se pintan. Los QR de prueba no cuentan.
 *
 * Como el resto del informe, cada pieza se llena al entrar en pantalla
 * (`useScrollRamp`): el waffle se puebla persona a persona, las barras y los
 * arcos crecen, las cifras cuentan y el visitante tipo se escribe. Al
 * imprimir todo termina de golpe.
 */
export function SurveyDemographics({
  eventIds,
  className,
}: {
  eventIds: readonly number[];
  className?: string;
}) {
  const t = useT();
  const enlacesQ = useScopeDeployments(eventIds);
  const enlaces = useMemo(() => enlacesQ.data ?? [], [enlacesQ.data]);

  const porEncuesta = useMemo(() => {
    const m = new Map<number, number[]>();
    for (const l of enlaces) m.set(l.surveyId, [...(m.get(l.surveyId) ?? []), l.id]);
    return m;
  }, [enlaces]);
  const surveyIds = useMemo(() => [...porEncuesta.keys()].sort((a, b) => a - b), [porEncuesta]);

  const agregadosQ = useQueries({
    queries: surveyIds.map((sid) => ({
      queryKey: queryKeys.xurvey.aggregate(sid, porEncuesta.get(sid) ?? []),
      queryFn: () => xurveyApi.aggregate(sid, { publicLinkIds: porEncuesta.get(sid) ?? [] }),
      staleTime: 60_000,
    })),
  });
  // Los nombres, sólo si el alcance mezcla encuestas: la misma consulta cacheada del Design Journey.
  const encuestasQ = useQuery({
    queryKey: ['xurvey', 'surveys', 'station'],
    queryFn: () => xurveyApi.listSurveys({ scope: 'station' }),
    staleTime: 60_000,
    enabled: surveyIds.length > 1,
  });
  const nombreDe = (sid: number) =>
    encuestasQ.data?.items.find((s) => s.id === sid)?.name ?? `#${sid}`;

  const cargando = enlacesQ.isLoading || agregadosQ.some((q) => q.isLoading);
  const agregados = agregadosQ.map((q) => q.data).filter((a): a is NonNullable<typeof a> => !!a);
  const completadas = agregados.reduce((s, a) => s + a.sessions.completed, 0);
  const dimensiones = useMemo(
    () => dimensionesDe(agregados.map((a) => ({ nombre: nombreDe(a.surveyId), components: a.components }))),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nombreDe depende de encuestasQ.data, ya incluido
    [agregados, encuestasQ.data],
  );

  const origen = dimensiones.find((d) => d.familia === 'origen');
  const resto = dimensiones.filter((d) => d !== origen);
  const conPersona = dimensiones.filter((d) => d.familia !== 'lista' && d.familia !== 'sino');

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-4 w-4" />
          {t('surveyDemographics.title')}
        </CardTitle>
        <CardDescription>
          {cargando
            ? t('common.loading')
            : enlaces.length === 0
              ? t('surveyDemographics.noLinks')
              : t('surveyDemographics.coverage', {
                  n: completadas.toLocaleString(),
                  links: String(enlaces.length),
                })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {cargando ? (
          <Skeleton className="h-40" />
        ) : enlaces.length === 0 ? null : completadas === 0 ? (
          <p className="text-sm text-[var(--color-fg-muted)]">{t('surveyDemographics.empty')}</p>
        ) : dimensiones.length === 0 ? (
          <p className="text-sm text-[var(--color-fg-muted)]">{t('surveyDemographics.noChoice')}</p>
        ) : (
          <>
            {(origen || conPersona.length > 0) && <FilaOrigen origen={origen} conPersona={conPersona} />}
            {resto.length > 0 && (
              <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
                {resto.map((d) => (
                  <Tile key={`${d.componente.componentId}`} d={d} />
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/* ── Piezas ──────────────────────────────────────────────────────────── */

/** Transición suave entre los fotogramas de la rampa (~20 fps). */
const EASE = 'transition-[width,height,opacity] duration-[600ms] [transition-timing-function:cubic-bezier(0.2,0,0,1)]';

/** La primera fila: el waffle de origen, su lista y el visitante tipo, con una sola rampa. */
function FilaOrigen({ origen, conPersona }: { origen: Dimension | undefined; conPersona: Dimension[] }) {
  const { ref, p } = useScrollRamp(1000);
  return (
    <div ref={ref} className="flex flex-wrap gap-5 rounded-xl border border-[var(--color-border-subtle)] p-4 transition-colors hover:border-[var(--color-xk-accent)]">
      {origen ? <Waffle d={origen} p={p} /> : null}
      <div className="flex min-w-[240px] flex-1 flex-col gap-3">
        {origen ? <Lista opciones={origen.opciones} p={p} /> : null}
        {conPersona.length > 0 ? <Persona dims={conPersona} /> : null}
      </div>
    </div>
  );
}

/**
 * La paleta del diseño (lima, oliva, menta, ámbar, pizarra), en tokens: la
 * lima es el acento de Xotski y el resto ya existe como semántico. Nada de hex.
 */
const PALETA = [
  'var(--color-xk-accent)',
  'var(--color-accent-ink)',
  'var(--color-success)',
  'var(--color-warn)',
  'var(--color-fg-muted)',
];
const color = (i: number) => PALETA[i % PALETA.length]!;

const EYEBROW =
  'font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--color-fg-muted)]';
const TILE =
  'flex flex-col gap-3 rounded-xl border border-[var(--color-border-subtle)] p-4 transition-colors hover:border-[var(--color-xk-accent)]';

function Encabezado({ d, extra }: { d: Dimension; extra?: string }) {
  const t = useT();
  return (
    <div className="flex flex-col gap-0.5">
      <span className={EYEBROW}>{extra ?? d.componente.label}</span>
      <span className="text-[11px] text-[var(--color-fg-subtle)]">
        {t('surveyDemographics.answered', { n: d.componente.answered.toLocaleString() })}
        {d.componente.multiple ? ` · ${t('surveyDemographics.multiple')}` : ''}
        {d.encuesta ? ` · ${t('surveyDemographics.fromSurvey', { name: d.encuesta })}` : ''}
      </span>
    </div>
  );
}

function Lista({ opciones, max = 6, p = 1 }: { opciones: OptionCount[]; max?: number; p?: number }) {
  const t = useT();
  const top = [...opciones].sort((a, b) => b.count - a.count);
  const visibles = top.slice(0, max);
  const otros = top.slice(max);
  const filas = otros.length
    ? [...visibles, { value: '__otros', label: t('surveyDemographics.other'), count: otros.reduce((s, o) => s + o.count, 0), pct: otros.reduce((s, o) => s + o.pct, 0) }]
    : visibles;
  return (
    <ul className="flex flex-col gap-1.5">
      {filas.map((o, i) => (
        <li key={o.value} className="flex items-center gap-2 text-[13px]">
          <span className="h-[11px] w-[11px] flex-none rounded-[3px]" style={{ background: color(i) }} />
          <span className="min-w-0 flex-1 truncate text-[var(--color-fg-primary)]">{o.label}</span>
          <span className="whitespace-nowrap font-mono text-[13px] font-bold tabular-nums">
            {(ramped(o.count, p) ?? 0).toLocaleString()}{' '}
            <span className="font-normal text-[var(--color-fg-muted)]">({Math.round(o.pct * p)}%)</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Cien personas, una celda cada una. Lo primero que se lee del informe. Se puebla con la rampa. */
function Waffle({ d, p = 1 }: { d: Dimension; p?: number }) {
  const t = useT();
  const top = [...d.opciones].sort((a, b) => b.count - a.count);
  const celdas = celdasWaffle(top);
  const colores: string[] = [];
  celdas.forEach((n, i) => {
    for (let k = 0; k < n; k++) colores.push(color(i));
  });
  const pobladas = Math.round(100 * p);
  return (
    <div className="flex flex-none flex-col gap-2.5">
      <span className={EYEBROW}>{t('surveyDemographics.per100', { label: d.componente.label })}</span>
      <div className="grid grid-cols-10 gap-[3px]" aria-hidden>
        {Array.from({ length: 100 }, (_, i) => (
          <span
            key={i}
            data-filled={i < pobladas ? 'true' : undefined}
            className="h-[15px] w-[15px] rounded-[3px] transition-colors duration-300"
            style={{ background: i < pobladas ? (colores[i] ?? 'var(--color-border-subtle)') : 'var(--color-border-subtle)' }}
          />
        ))}
      </div>
    </div>
  );
}

/** El bloque negro con lima del diseño: la respuesta más frecuente a cada pregunta, en una frase. */
function Persona({ dims }: { dims: Dimension[] }) {
  const t = useT();
  const partes = dims.map(dominante).filter((o): o is OptionCount => !!o);
  const nota = dims
    .map((d) => {
      const tit = titularDe(d);
      return tit ? `${tit.pct}% ${tit.label}` : null;
    })
    .filter(Boolean)
    .join(' · ');
  const titular = partes.map((o) => o.label).join(' · ');
  const { ref, text, typing } = useTypewriter(titular, { step: 2, intervalMs: 28 });
  const escrito = !typing && text.length === titular.length;
  return (
    <div ref={ref} className="flex flex-col gap-1.5 rounded-xl bg-[var(--color-chrome-bg)] p-4">
      <span className="font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--color-xk-accent)]">
        {t('surveyDemographics.persona')}
      </span>
      <span className="min-h-[1.4em] text-[19px] font-bold leading-snug text-[var(--color-chrome-fg)] [text-wrap:pretty]">
        {text}
        {typing ? <span aria-hidden className="ml-0.5 inline-block w-[2px] bg-[var(--color-xk-accent)] [animation:dash-caret_1s_steps(1)_infinite]">&nbsp;</span> : null}
      </span>
      <span className={cn('text-[13px] text-[var(--color-chrome-fg-muted)] [text-wrap:pretty] transition-opacity duration-500', escrito ? 'opacity-100' : 'opacity-0')}>
        {nota || t('surveyDemographics.personaNote')}
      </span>
    </div>
  );
}

function Tile({ d }: { d: Dimension }) {
  switch (d.familia) {
    case 'edad':
      return <Escalera d={d} />;
    case 'pais':
      return <Rosquilla d={d} />;
    case 'genero':
      return <Balanza d={d} />;
    case 'sino':
      return <Medidor d={d} />;
    default:
      return <TileLista d={d} />;
  }
}

function TileLista({ d }: { d: Dimension }) {
  const { ref, p } = useScrollRamp();
  return (
    <div ref={ref} className={TILE}>
      <Encabezado d={d} />
      <Barras opciones={d.opciones} p={p} />
    </div>
  );
}

/** Edad: una barra por franja, en orden de franja; la mayor en lima. */
function Escalera({ d }: { d: Dimension }) {
  const t = useT();
  const { ref, p } = useScrollRamp();
  const max = Math.max(1, ...d.opciones.map((o) => o.count));
  const tit = titularDe(d);
  return (
    <div ref={ref} className={TILE}>
      <Encabezado d={d} />
      <div className="flex h-[150px] items-end gap-2">
        {d.opciones.map((o) => {
          const caliente = o.count === max;
          return (
            <div key={o.value} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1.5" title={`${o.label}: ${o.count} (${Math.round(o.pct)}%)`}>
              <span className={cn('font-mono text-[12px] font-bold tabular-nums', caliente ? 'text-[var(--color-accent-ink)]' : 'text-[var(--color-fg-primary)]')}>
                {(ramped(o.count, p) ?? 0).toLocaleString()}
              </span>
              <div
                className={cn('w-full rounded-t', EASE)}
                style={{ height: `${Math.max(2, (o.count / max) * 100) * p}%`, background: caliente ? 'var(--color-xk-accent)' : 'var(--color-accent-ink)' }}
              />
              <span className="max-w-full truncate font-mono text-[10px] text-[var(--color-fg-muted)]">{o.label}</span>
            </div>
          );
        })}
      </div>
      {tit ? (
        <p className="text-[13px] leading-snug text-[var(--color-fg-primary)]">
          {t('surveyDemographics.largest', { label: tit.label, pct: String(tit.pct) })}
        </p>
      ) : null}
    </div>
  );
}

/** País: rosquilla por partes, con la mayor en el centro. */
function Rosquilla({ d }: { d: Dimension }) {
  const { ref, p } = useScrollRamp();
  const top = [...d.opciones].sort((a, b) => b.count - a.count);
  const total = top.reduce((s, o) => s + o.count, 0) || 1;
  let acc = 0;
  // Los arcos barren con la rampa: cada parada se escala por `p`.
  const paradas = top.slice(0, 5).map((o, i) => {
    const desde = acc;
    acc += (o.count / total) * 100 * p;
    return `${color(i)} ${desde.toFixed(1)}% ${acc.toFixed(1)}%`;
  });
  const fondo = `conic-gradient(${paradas.join(', ')}, var(--color-border-subtle) ${acc.toFixed(1)}% 100%)`;
  const primero = top[0];
  return (
    <div ref={ref} className={TILE}>
      <Encabezado d={d} />
      <div className="flex justify-center py-1">
        <div className="relative h-[150px] w-[150px] rounded-full" style={{ background: fondo }} aria-hidden>
          <div className="absolute inset-[30px] flex flex-col items-center justify-center rounded-full bg-[var(--color-bg-surface)]">
            <span className="font-mono text-[22px] font-bold tabular-nums">{primero ? `${Math.round((primero.count / total) * 100 * p)}%` : '—'}</span>
            <span className="max-w-[80px] truncate font-mono text-[9px] uppercase tracking-[0.1em] text-[var(--color-fg-muted)]">{primero?.label}</span>
          </div>
        </div>
      </div>
      <Lista opciones={d.opciones} max={5} p={p} />
    </div>
  );
}

/** Género: una barra repartida, con el sesgo dicho en un chip. */
function Balanza({ d }: { d: Dimension }) {
  const t = useT();
  const { ref, p } = useScrollRamp();
  const total = d.opciones.reduce((s, o) => s + o.count, 0) || 1;
  const top = dominante(d);
  const sesgo = top && top.count / total >= 0.55 ? t('surveyDemographics.lean', { label: top.label }) : t('surveyDemographics.even');
  const primero = d.opciones[0];
  const ultimo = d.opciones[d.opciones.length - 1];
  return (
    <div ref={ref} className={TILE}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Encabezado d={d} />
        <span className="rounded-full bg-[var(--color-accent-15)] px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.1em] text-[var(--color-accent-ink)]">
          {sesgo}
        </span>
      </div>
      <div className="relative flex h-10 overflow-hidden rounded-lg bg-[var(--color-bg-elevated)]">
        {d.opciones.map((o, i) => {
          const w = (o.count / total) * 100;
          return (
            <div key={o.value} className={cn('flex h-full items-center justify-center overflow-hidden whitespace-nowrap', EASE)} style={{ width: `${w * p}%`, background: color(i) }} title={`${o.label}: ${o.count}`}>
              {w >= 12 ? (
                <span className={cn('font-mono text-[12px] font-bold', i === 0 ? 'text-[var(--color-fg-on-accent)]' : 'text-white')}>{Math.round(w)}%</span>
              ) : null}
            </div>
          );
        })}
        <span className="absolute inset-y-0 left-1/2 w-0.5 bg-[var(--color-fg-primary)]" aria-hidden />
      </div>
      <div className="flex justify-between gap-2 font-mono text-[11px] text-[var(--color-fg-muted)]">
        <span>{primero ? `${(ramped(primero.count, p) ?? 0).toLocaleString()} ${primero.label}` : ''}</span>
        <span>{ultimo && ultimo !== primero ? `${(ramped(ultimo.count, p) ?? 0).toLocaleString()} ${ultimo.label}` : ''}</span>
      </div>
    </div>
  );
}

/** Sí/no: medio anillo con el «sí» en lima. */
function Medidor({ d }: { d: Dimension }) {
  const t = useT();
  const { ref, p } = useScrollRamp();
  const total = d.opciones.reduce((s, o) => s + o.count, 0) || 1;
  const si = d.opciones.find((o) => /^(yes|s[ií]|true)$/i.test(o.label) || /^(yes|s[ií]|true)$/i.test(o.value));
  const pct = si ? Math.round((si.count / total) * 100 * p) : 0;
  const fondo = `conic-gradient(from 270deg, var(--color-xk-accent) 0 ${(pct / 2).toFixed(1)}%, var(--color-border-subtle) ${(pct / 2).toFixed(1)}% 50%, transparent 50% 100%)`;
  return (
    <div ref={ref} className={TILE}>
      <Encabezado d={d} />
      <div className="flex justify-center">
        <div className="relative h-[100px] w-[190px] overflow-hidden" aria-hidden>
          <div className="absolute left-0 top-0 h-[190px] w-[190px] rounded-full" style={{ background: fondo }} />
          <div className="absolute left-[32px] top-[32px] h-[126px] w-[126px] rounded-full bg-[var(--color-bg-surface)]" />
          <div className="absolute inset-x-0 bottom-0 flex flex-col items-center">
            <span className="font-mono text-[28px] font-bold leading-none tabular-nums">{pct}%</span>
            <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-[var(--color-fg-muted)]">{t('surveyDemographics.sayYes')}</span>
          </div>
        </div>
      </div>
      <Lista opciones={d.opciones} max={3} p={p} />
    </div>
  );
}

/** Lo demás: cada opción con su barra, del mayor al menor. */
function Barras({ opciones, p = 1 }: { opciones: OptionCount[]; p?: number }) {
  const top = [...opciones].sort((a, b) => b.count - a.count).slice(0, 8);
  const max = Math.max(1, ...top.map((o) => o.count));
  return (
    <ul className="flex flex-col gap-2">
      {top.map((o, i) => (
        <li key={o.value} className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-2 text-[13px]">
            <span className="min-w-0 flex-1 truncate">{o.label}</span>
            <span className="whitespace-nowrap font-mono text-[12px] font-bold tabular-nums">
              {(ramped(o.count, p) ?? 0).toLocaleString()} <span className="font-normal text-[var(--color-fg-muted)]">({Math.round(o.pct * p)}%)</span>
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-[var(--color-bg-elevated)]">
            <div className={cn('h-full rounded-full', EASE)} style={{ width: `${(o.count / max) * 100 * p}%`, background: color(i) }} />
          </div>
        </li>
      ))}
    </ul>
  );
}
